/*
 * Darstellung: persönliches Hell/Dunkel/System und die Markenfarbe des aktuellen Kontexts.
 * Variablen liegen an <html>, damit auch Dialoge und Menüs in Portalen die richtige Gestaltung
 * erhalten (T05). Beim Verlassen eines Boards gilt wieder die Installation (B03).
 */
import { useEffect, useSyncExternalStore } from 'react';
import { accentTokens, type ResolvedBranding, type ThemeChoice } from '@kaba/shared';

const KEY = 'kaba.theme';
const root = () => document.documentElement;
const mq = typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)') : null;

let choice: ThemeChoice = readStored();
let mode: 'light' | 'dark' = computeMode();
const listeners = new Set<() => void>();

function readStored(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}
function computeMode(): 'light' | 'dark' {
  return choice === 'system' ? (mq?.matches ? 'dark' : 'light') : choice;
}
function apply() {
  const next = computeMode();
  const changed = next !== mode || root().dataset.theme !== next;
  mode = next;
  root().dataset.theme = mode;
  document.querySelector('meta[name="theme-color"]:not([media])')?.setAttribute('content', getComputedStyle(root()).getPropertyValue('--surface').trim() || (mode === 'dark' ? '#14161a' : '#f6f7f9'));
  if (changed) {
    writeAccent();
    listeners.forEach((l) => l());
  }
}
mq?.addEventListener('change', () => choice === 'system' && apply());

/** Persönliche Wahl übernehmen (Konto) und lokal für den nächsten Start merken */
export function setThemeChoice(c: ThemeChoice) {
  choice = c;
  try {
    if (c === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, c);
  } catch {
    /* privat */
  }
  apply();
}
export const themeChoice = () => choice;

export function useThemeMode(): 'light' | 'dark' {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => mode,
  );
}

/* ---------- Markenfarbe ---------- */

let instanceAccent: string | null = null;
/** Stapel der aktiven Kontexte (Board, Team, Vorschau); der oberste gilt */
const stack: { id: number; accent: string | null }[] = [];
let seq = 0;

function writeAccent() {
  const hex = stack.length ? stack[stack.length - 1]!.accent : instanceAccent;
  const t = accentTokens(hex, mode);
  const s = root().style;
  if (!hex) {
    // Ohne Marke: Standardwerte aus index.css
    for (const k of ['--color-accent', '--color-on-accent', '--color-accent-fg', '--color-accent-soft', '--color-header-tint']) s.removeProperty(k);
    return;
  }
  s.setProperty('--color-accent', t.accent);
  s.setProperty('--color-on-accent', t.onAccent);
  s.setProperty('--color-accent-fg', t.text);
  s.setProperty('--color-accent-soft', t.soft);
  s.setProperty('--color-header-tint', t.tint);
}

export function setInstanceAccent(hex: string | null) {
  if (hex === instanceAccent) return;
  instanceAccent = hex;
  writeAccent();
}

/** Markenfarbe eines Kontexts, solange die Komponente eingehängt ist */
export function useAccentScope(hex: string | null | undefined, active = true) {
  useEffect(() => {
    if (!active || hex === undefined) return;
    const entry = { id: ++seq, accent: hex };
    stack.push(entry);
    writeAccent();
    return () => {
      const i = stack.findIndex((x) => x.id === entry.id);
      if (i >= 0) stack.splice(i, 1);
      writeAccent();
    };
  }, [hex, active]);
}

/** Board-Kontext: Farbe und Kopfstil */
export function useBoardBranding(b: ResolvedBranding | undefined) {
  useAccentScope(b ? b.accent : undefined);
}

apply();
